import cx from 'classnames';
import { useCallback, useState, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import CheckboxUncheckedIcon from '@atlaskit/icon/core/checkbox-unchecked';
import { Analytics } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';

import type { FeatureNames } from './featuresInfo';
import { useFeedbackCollector } from './useFeedbackCollector';

import * as styles from './NegativeFeedbackDetail.module.less';

interface NegativeFeedbackDetailProps {
  featureName: FeatureNames;
  feedbackSubmissionCallback?: () => void;
  traceId?: string;
  flagTreatment?: boolean;
  attributes?: Record<string, string>;
}

/**
 * Displays a set of checkboxes representing negative-feedback reasons for an
 * AI Labs feature and a submit button that fires an analytics event with the
 * selected reasons.
 *
 * The available reasons (facets) are derived from the feature's configuration
 * via {@link useFeedbackCollector}. On submission the component sends a
 * `clickedButton` analytics event, resets the selection, and invokes the
 * optional `feedbackSubmissionCallback`.
 *
 * @param featureName - Identifies the AI Labs feature being rated; used to
 *   look up the relevant negative facets.
 * @param feedbackSubmissionCallback - Optional callback invoked after feedback
 *   is submitted successfully.
 * @param traceId - Optional trace identifier attached to the analytics event
 *   for correlating feedback with a specific AI interaction.
 * @param flagTreatment - When `true`, applies an alternative visual treatment
 *   to the container.
 * @param attributes - Optional extra per-instance context merged into the
 *   submitted feedback's analytics attributes.
 */
export const NegativeFeedbackDetail: FunctionComponent<
  NegativeFeedbackDetailProps
> = ({
  featureName,
  feedbackSubmissionCallback,
  traceId,
  flagTreatment,
  attributes,
}) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const { negativeFacets, getAnalyticsAttributes } = useFeedbackCollector({
    featureName,
  });

  const submitFeedback = useCallback(() => {
    setSelectedReasons([]);

    //TODO: wire up Jira feedback collector to gather the freeform feedback
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'aiLabsFeedbackCollectorSubmitFeedbackButton',
      source: 'aiLabsFeedbackCollector',
      attributes: {
        ...attributes,
        ...getAnalyticsAttributes({
          numNegativeFacets: selectedReasons.length,
        }),
        reason: selectedReasons,
        taskId: traceId,
      },
    });

    feedbackSubmissionCallback?.();
  }, [
    attributes,
    feedbackSubmissionCallback,
    getAnalyticsAttributes,
    selectedReasons,
    traceId,
  ]);

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked && !selectedReasons.includes(e.target.value)) {
        setSelectedReasons((prev) => [...prev, e.target.value]);
      } else if (
        !e.target.checked &&
        selectedReasons.includes(e.target.value)
      ) {
        setSelectedReasons((prev) =>
          prev.filter((reason) => reason !== e.target.value),
        );
      }
    },
    [selectedReasons],
  );

  if (negativeFacets.length === 0) {
    return null;
  }

  return (
    <div
      className={cx(styles.feedbackDetailContainer, {
        [styles.flagTreatment]: flagTreatment,
      })}
    >
      <fieldset>
        <div className={styles.contentContainer}>
          <div className={styles.reasonsContainer}>
            {negativeFacets.map((facet) => (
              <label className={styles.reason} key={facet.name}>
                <input
                  type="checkbox"
                  name={facet.name}
                  value={facet.name}
                  onChange={handleReasonChange}
                  checked={selectedReasons.includes(facet.name)}
                />
                {!selectedReasons.includes(facet.name) && (
                  <CheckboxUncheckedIcon label="" size="small" aria-hidden />
                )}
                {selectedReasons.includes(facet.name) && (
                  <CheckboxCheckedIcon label="" size="small" aria-hidden />
                )}
                {facet.label}
              </label>
            ))}
          </div>
        </div>

        <Button
          className={styles.submitButton}
          onClick={submitFeedback}
          appearance="primary"
        >
          <FormattedMessage
            id="templates.ai-labs.feedback-submit-button"
            defaultMessage="Submit feedback"
            description="Button to submit negative feedback"
          />
        </Button>
      </fieldset>
    </div>
  );
};
